import {
    accountSourceDataTypeChecker,
    AccountSourceType,
    addAccount,
    getAccountBySourceId,
    updateAccount,
} from 'owa-account-source-list-store';
import type { MailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyEnsureAdditionalAccountInfoData } from '../lazyIndex';
import { HostSourceInformation } from '../types';
import {
    buildM365ConnectedMailboxAccountSourceFromHostSourceInformation,
    buildM365UserAccountSourceFromHostSourceInformation,
} from './accountSourceBuilder';
import { buildSourceId, globalProfile, microsoft365SourceType } from './sourceIdBuilder';

/**
 * Returns the MailboxInfo for the specified sourceId
 * @param sourceId the sourceId for the account
 * @returns The mailbox information, or undefined if the account is not a mailbox
 */
export function getMailboxBySourceId(sourceId: string): MailboxInfo | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    return accountSourceDataTypeChecker.isM365Mailbox(sourceInfo)
        ? {
              sourceId,
              type: sourceInfo.m365mailbox.type,
              userIdentity: sourceInfo.m365mailbox.userIdentity,
              mailboxSmtpAddress: sourceInfo.m365mailbox.emailAddress,
          }
        : undefined;
}

export function getMailboxTypeBySourceId(sourceId: string): string | undefined {
    const sourceInfo = getAccountBySourceId(sourceId);
    switch (sourceInfo?.sourceType) {
        case AccountSourceType.Office365:
            return 'OFFICE365';

        case AccountSourceType.OutlookDotCom:
            return 'OUTLOOKDOTCOM';

        default:
            return undefined;
    }
}

/**
 * convert the given account (HostSourceInformation) to M365UserMailboxAccountSource
 * and adds to the source list store, and returns the sourceId
 * @param account
 */
export function addM365UserMailboxHostAccount(account: HostSourceInformation): string {
    if (!account.mailbox) {
        throw new Error('Can not create a Connected Mailbox for an account that has no mailbox');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox.userIdentity
    );

    if (!getAccountBySourceId(sourceId)) {
        const accountSource = buildM365UserAccountSourceFromHostSourceInformation(
            sourceId,
            account
        );
        addAccount(accountSource);
    }

    return sourceId;
}

/**
 * convert the given account (HostSourceInformation) to M365UserMailboxHostAccountSource
 * and update the source list store with the converted account, and returns the sourceId
 * @param account
 */
export function updateM365UserMailboxHostAccount(account: HostSourceInformation): string {
    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox!.userIdentity
    );

    if (getAccountBySourceId(sourceId)) {
        updateAccount(buildM365UserAccountSourceFromHostSourceInformation(sourceId, account));
        const updatedAccount = getAccountBySourceId(sourceId);

        if (isFeatureEnabled('acct-sourceIdAwareGroupsStore')) {
            lazyEnsureAdditionalAccountInfoData.importAndExecute(
                updatedAccount!,
                true /** forceUpdate */
            );
        }
    }

    return sourceId;
}

/**
 * convert the given account (HostSourceInformation) to M365ConnectedMailboxAccountSource
 * and adds to the source list store, and returns the sourceId
 * @param account
 */
export function addM365ConnectedMailboxHostAccount(account: HostSourceInformation): string {
    if (!account.mailbox) {
        throw new Error('Can not create a Connected Mailbox for an account that has no mailbox');
    }

    const sourceId = buildSourceId(
        globalProfile,
        microsoft365SourceType,
        account.mailbox.userIdentity
    );

    if (!getAccountBySourceId(sourceId)) {
        const accountSource = buildM365ConnectedMailboxAccountSourceFromHostSourceInformation(
            sourceId,
            account
        );
        addAccount(accountSource);
    }

    return sourceId;
}
